import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Link, useLocation } from 'react-router-dom';
import { getBackendUrl } from '../config';

const BACKEND_URL = getBackendUrl();

const SEVERITY_COLORS = {
  LOW: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
  MEDIUM: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  HIGH: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  CRITICAL: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

const SECTION_QUERY_VALUES = ['active', 'historical', 'notifications', 'reports', 'pi'];

const Alarms = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const DEVICE_TYPE_LABELS = { DATA_LOGGER: t('dataLogger'), INVERTER: t('inverter'), ENERGY_METER: t('energyMeter') };
  const [activeSection, setActiveSection] = useState('active'); // active | historical | notifications | reports
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterActiveOnly, setFilterActiveOnly] = useState(true);
  const [activeDeviceType, setActiveDeviceType] = useState('');
  const [historicalDeviceType, setHistoricalDeviceType] = useState('');
  const [historicalStateFilter, setHistoricalStateFilter] = useState('ALL'); // ALL | ON | OFF
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifForm, setNotifForm] = useState({ email: '', phone: '', company_id: '' });
  const [companies, setCompanies] = useState([]);
  // Alarmet nga Pi (pi-alarms-inverter): për çdo inverter, lista payload.alarms [{ no, name, state }]
  const [piAlarmsList, setPiAlarmsList] = useState([]); // [{ inverter: { id, name, ... }, alarmList: [...], ts }]
  const [piAlarmsLoading, setPiAlarmsLoading] = useState(false);
  const [piLastFetchMs, setPiLastFetchMs] = useState(0);

  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const s = q.get('section');
    if (s && SECTION_QUERY_VALUES.includes(s)) {
      setActiveSection(s);
    }
  }, [location.search]);

  useEffect(() => {
    if (activeSection === 'active') {
      fetchAlarms('active');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, filterActiveOnly, activeDeviceType]);

  useEffect(() => {
    if (activeSection === 'historical') {
      fetchAlarms('historical');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, historicalDeviceType, historicalStateFilter]);

  useEffect(() => {
    if (activeSection === 'notifications') {
      fetchNotifications();
      fetchCompanies();
    }
  }, [activeSection]);

  useEffect(() => {
    // Throttle: mos e rifetch vazhdimisht kur kalon shpejt mes tabs.
    if (activeSection !== 'pi') return;
    const now = Date.now();
    if (now - piLastFetchMs < 30000 && piAlarmsList.length > 0) return;
    fetchPiAlarms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, piLastFetchMs, piAlarmsList.length]);

  const fetchAlarms = async (mode = 'active') => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams();
      const isHistorical = mode === 'historical';
      const deviceType = isHistorical ? historicalDeviceType : activeDeviceType;
      let list = [];
      try {
        if (isHistorical) {
          if (deviceType) params.set('device_type', deviceType);
          params.set('state', historicalStateFilter || 'ALL');
          params.set('limit', '400');
          const res = await axios.get(`${BACKEND_URL}/api/alarms/pi-history?${params}`, { headers });
          list = Array.isArray(res.data?.alarms) ? res.data.alarms : [];
        } else {
          if (filterActiveOnly) params.set('active_only', 'true');
          if (deviceType) params.set('device_type', deviceType);
          const res = await axios.get(`${BACKEND_URL}/api/alarms?${params}`, { headers });
          list = Array.isArray(res.data) ? res.data : [];
        }
      } catch (e) {
        console.error('Alarms fetch error:', e);
      }
      // Statuset nga Pi shfaqen vetëm te seksioni "Status From Pi" (pa merge këtu),
      // që të shmangim thirrje të shumta dhe të panevojshme.
      setAlarms(list);
    } catch (e) {
      setAlarms([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    setNotifLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const res = await axios.get(`${BACKEND_URL}/api/alarm-notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Notifications fetch error:', e);
      setNotifications([]);
    } finally {
      setNotifLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const res = await axios.get(`${BACKEND_URL}/api/companies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanies(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setCompanies([]);
    }
  };

  const fetchPiAlarms = async () => {
    if (piAlarmsLoading) return;
    setPiAlarmsLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      const invRes = await axios.get(`${BACKEND_URL}/api/devices/inverters`, { headers });
      const inverters = Array.isArray(invRes.data) ? invRes.data : [];
      const list = [];
      for (const inv of inverters.slice(0, 50)) {
        try {
          // Kërkojmë vetëm dokumentin më të fundit të PI alarms.
          const res = await axios.get(`${BACKEND_URL}/api/devices/${inv.id}/pi-alarms-inverter?limit=1`, { headers });
          const docs = res.data?.alarms ?? [];
          const latest = docs[0];
          const rawAlarmList = Array.isArray(latest?.payload?.alarms) ? latest.payload.alarms : [];
          const alarmList = rawAlarmList.map((a) => ({
            ...a,
            state: String(a?.state || '').toUpperCase() === 'ON' ? 'ON' : 'OFF',
          }));
          list.push({ inverter: inv, alarmList, ts: latest?.ts });
        } catch (_) {
          list.push({ inverter: inv, alarmList: [], ts: null });
        }
      }
      setPiAlarmsList(list);
      setPiLastFetchMs(Date.now());
    } catch (e) {
      console.error('Pi alarms fetch error:', e);
      setPiAlarmsList([]);
    } finally {
      setPiAlarmsLoading(false);
    }
  };

  const acknowledgeAlarm = async (alarmId) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(`${BACKEND_URL}/api/alarms/${alarmId}/acknowledge`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchAlarms(activeSection === 'historical' ? 'historical' : 'active');
    } catch (e) {
      console.error('Acknowledge error:', e);
    }
  };

  const submitNotification = async (e) => {
    e.preventDefault();
    if (!notifForm.email?.trim()) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${BACKEND_URL}/api/alarm-notifications`, {
        email: notifForm.email?.trim(),
        company_id: notifForm.company_id || undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setNotifForm({ email: '', company_id: '' });
      fetchNotifications();
    } catch (e) {
      console.error('Subscribe error:', e);
      toast.error(e.response?.data?.detail || t('registerError'));
    }
  };

  const deleteNotification = async (id) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${BACKEND_URL}/api/alarm-notifications/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchNotifications();
    } catch (e) {
      console.error('Delete notification error:', e);
    }
  };

  const sections = [
    { id: 'active', labelKey: 'activeAlarms', icon: '✓', descKey: 'activeAlarmsList' },
    { id: 'pi', labelKey: 'statusFromPi', icon: '⚡', descKey: 'alarmsFromInverters' },
    { id: 'historical', labelKey: 'history', icon: '📊', descKey: 'alarmHistory' },
    { id: 'notifications', labelKey: 'notifications', icon: '🔔', descKey: 'registerNotif' },
    { id: 'reports', labelKey: 'reportsSection', icon: '📄', descKey: 'alarmReports' },
  ];

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    return d.toLocaleString('sq-AL', { dateStyle: 'short', timeStyle: 'short' });
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header + lightweight card (no orange) */}
        <div className="p-4 md:p-6">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-4 md:mb-6">{t('alarmSystem')}</h1>

          {/* Section tabs - responsive grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-4 mb-6">
            {sections.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActiveSection(s.id)}
                className={`rounded-xl p-4 md:p-5 text-left transition border-2 ${activeSection === s.id
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
              >
                <span className="text-2xl md:text-3xl mb-2 block" aria-hidden>{s.icon}</span>
                <span className="font-semibold text-sm md:text-base block">{s.labelKey ? t(s.labelKey) : s.label}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 hidden md:block">{s.descKey ? t(s.descKey) : s.desc}</span>
              </button>
            ))}
          </div>

          {/* Content area - pa kuti të bardhë, i njëjtë bg si faqja (web + mobile) */}
          <div className="overflow-hidden">
            {/* Active Alarms */}
            {activeSection === 'active' && (
              <div className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={filterActiveOnly}
                      onChange={(e) => setFilterActiveOnly(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('activeOnly')}</span>
                  </label>
                  <select
                    value={activeDeviceType}
                    onChange={(e) => setActiveDeviceType(e.target.value)}
                    className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 max-w-xs"
                  >
                    <option value="">{t('allDevicesFilter')}</option>
                    <option value="DATA_LOGGER">{t('dataLogger')}</option>
                    <option value="INVERTER">{t('inverter')}</option>
                    <option value="ENERGY_METER">{t('energyMeter')}</option>
                  </select>
                </div>
                {loading ? (
                  <p className="text-gray-500 dark:text-gray-400 py-8">{t('loading')}</p>
                ) : alarms.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 py-8">{t('noAlarms')}</p>
                ) : (
                  <ul className="space-y-3">
                    {alarms.map((a) => (
                      <li
                        key={a.id}
                        className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600"
                      >
                        <div className="flex-1 min-w-0">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium mr-2 ${SEVERITY_COLORS[a.severity] || SEVERITY_COLORS.MEDIUM}`}>
                            {a.severity}
                          </span>
                          {a.device_type && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">
                              {DEVICE_TYPE_LABELS[a.device_type] || a.device_type}
                            </span>
                          )}
                          {a._fromPi && (
                            <span className="text-xs text-blue-600 dark:text-blue-400 mr-2">{t('fromPi')}</span>
                          )}
                          <p className="font-medium text-gray-900 dark:text-white truncate">{a.message}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {a.device_name || a.device_id} · {formatDate(a.timestamp)}
                          </p>
                        </div>
                        {a.is_active && !a.acknowledged && !a._fromPi && (
                          <button
                            type="button"
                            onClick={() => acknowledgeAlarm(a.id)}
                            className="shrink-0 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
                          >
                            {t('acknowledge')}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Statusi nga Pi – payload.alarms nga pi-alarms-inverter */}
            {activeSection === 'pi' && (
              <div className="p-4 md:p-6">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {t('alarmStatusFromPi')}
                </p>
                {piAlarmsLoading ? (
                  <p className="text-gray-500 dark:text-gray-400 py-8">{t('loading')}</p>
                ) : piAlarmsList.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 py-8">{t('noInvertersData')}</p>
                ) : (
                  <div className="space-y-6">
                    {piAlarmsList.map(({ inverter, alarmList, ts }) => (
                      <div
                        key={inverter.id}
                        className="rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 overflow-hidden"
                      >
                        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-600 flex flex-wrap items-center justify-between gap-2">
                          <h3 className="font-semibold text-gray-900 dark:text-white">{inverter.name || inverter.serial_number || inverter.id}</h3>
                          {ts && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {t('updatedLabel')} {(() => {
                                const d = new Date(ts);
                                return isNaN(d.getTime()) ? '—' : d.toLocaleString('sq-AL', { dateStyle: 'short', timeStyle: 'short' });
                              })()}
                            </span>
                          )}
                        </div>
                        <div className="p-4">
                          {alarmList.length === 0 ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400">{t('noAlarmList')}</p>
                          ) : (
                            <ul className="space-y-2 max-h-64 overflow-y-auto">
                              {alarmList.map((a) => {
                                const isOn = String(a?.state || '').toUpperCase() === 'ON';
                                return (
                                  <li
                                    key={a?.no ?? a?.name}
                                    className="flex justify-between items-center py-2 px-3 rounded-lg text-sm bg-gray-50 dark:bg-gray-700/50"
                                  >
                                    <span className="font-medium text-gray-900 dark:text-white">{a?.name ?? '—'}</span>
                                    <span
                                      className={`px-2.5 py-0.5 rounded-md text-xs font-medium ${isOn ? 'bg-red-500/20 text-red-500 dark:text-red-400' : 'bg-green-500/20 text-green-600 dark:text-green-400'}`}
                                    >
                                      {a?.state ?? 'OFF'}
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Historical */}
            {activeSection === 'historical' && (
              <div className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                  <select
                    value={historicalStateFilter}
                    onChange={(e) => setHistoricalStateFilter(e.target.value)}
                    className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 max-w-xs"
                  >
                    <option value="ALL">Statusi: Të gjitha</option>
                    <option value="ON">Statusi: ON (Aktive)</option>
                    <option value="OFF">Statusi: OFF (Jo aktive)</option>
                  </select>
                  <select
                    value={historicalDeviceType}
                    onChange={(e) => setHistoricalDeviceType(e.target.value)}
                    className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 max-w-xs"
                  >
                    <option value="">{t('allDevicesFilter')}</option>
                    <option value="DATA_LOGGER">{t('dataLogger')}</option>
                    <option value="INVERTER">{t('inverter')}</option>
                    <option value="ENERGY_METER">{t('energyMeter')}</option>
                  </select>
                </div>
                {loading ? (
                  <p className="text-gray-500 dark:text-gray-400 py-8">{t('loading')}</p>
                ) : alarms.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 py-8">{t('noHistoricalData')}</p>
                ) : (
                  <ul className="space-y-3">
                    {alarms.map((a) => (
                      <li
                        key={a.id}
                        className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600"
                      >
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium mr-2 ${SEVERITY_COLORS[a.severity] || SEVERITY_COLORS.MEDIUM}`}>
                          {a.severity}
                        </span>
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium mr-2 ${a.is_active ? 'bg-red-500/20 text-red-600 dark:text-red-400' : 'bg-green-500/20 text-green-600 dark:text-green-400'}`}>
                          {a.is_active ? 'ON' : 'OFF'}
                        </span>
                        {a.device_type && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">
                            {DEVICE_TYPE_LABELS[a.device_type] || a.device_type}
                          </span>
                        )}
                        <p className="font-medium text-gray-900 dark:text-white mt-1">{a.message}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {a.device_name || a.device_id} · {formatDate(a.timestamp)}
                          {a.acknowledged && ` · ${t('acknowledged')}`}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Notifications - Email */}
            {activeSection === 'notifications' && (
              <div className="p-4 md:p-6">
                <form onSubmit={submitNotification} className="space-y-4 max-w-md mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                    <input
                      type="email"
                      value={notifForm.email}
                      onChange={(e) => setNotifForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="email@shembull.com"
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
                    />
                  </div>
                  {user?.role === 'SUPERADMIN' && companies.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('companyOptional')}</label>
                      <select
                        value={notifForm.company_id}
                        onChange={(e) => setNotifForm((f) => ({ ...f, company_id: e.target.value }))}
                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
                      >
                        <option value="">{t('all')}</option>
                        {companies.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={!notifForm.email?.trim()}
                    className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium"
                  >
                    {t('registerNotifications')}
                  </button>
                </form>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">{t('myRegistrations')}</h3>
                  {notifLoading ? (
                    <p className="text-gray-500 text-sm">{t('loading')}</p>
                  ) : notifications.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400 text-sm">{t('noRegistrations')}</p>
                  ) : (
                    <ul className="space-y-2">
                      {notifications.map((n) => (
                        <li key={n.id} className="flex flex-wrap items-center gap-2 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-sm">
                          {n.email && <span className="text-gray-700 dark:text-gray-300">{n.email}</span>}
                          <button
                            type="button"
                            onClick={() => deleteNotification(n.id)}
                            className="ml-auto text-red-600 dark:text-red-400 hover:underline text-xs"
                          >
                            {t('delete')}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {/* Reports - link to Reports page */}
            {activeSection === 'reports' && (
              <div className="p-4 md:p-6">
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {t('alarmReportsHint')}
                </p>
                <Link
                  to="/reports"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
                >
                  {t('openReports')}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Alarms;
